import { Graphics, Text, useTick } from "@pixi/react"
import { type Graphics as PixiGraphics, TextStyle } from "pixi.js"
import { useRef } from "react"

interface CanvasLoadingProps {
  x: number
  y: number
  text?: string
}

const SPINNER_COLOR = 0x087c61
const SPINNER_RADIUS = 7
const SPINNER_LINE_WIDTH = 2
const SPINNER_ALPHA = 1
const SPINNER_SEGMENTS = 30
const SPINNER_ROTATION_SPEED = 0.1

const spinnerTextStyle = new TextStyle({
  fill: 0x000000,
  fontSize: 16,
  fontFamily: getComputedStyle(document.documentElement).getPropertyValue("--fr-body-font-family").trim()
})

const drawArc = (
  g: PixiGraphics,
  radius: number,
  startAngle: number,
  endAngle: number,
  segments = SPINNER_SEGMENTS
) => {
  const angleStep = (endAngle - startAngle) / segments
  g.moveTo(Math.cos(startAngle) * radius, Math.sin(startAngle) * radius)

  for (let i = 1; i <= segments; i++) {
    const angle = startAngle + i * angleStep
    const x = Math.cos(angle) * radius
    const y = Math.sin(angle) * radius
    g.lineTo(x, y)
  }
}

function Spinner({ x, y }: { x: number; y: number }) {
  const rotationRef = useRef(0)
  const graphicsRef = useRef<PixiGraphics>(null)

  useTick((delta) => {
    rotationRef.current += SPINNER_ROTATION_SPEED * delta
    if (graphicsRef.current) {
      graphicsRef.current.rotation = rotationRef.current
    }
  })

  const draw = (g: PixiGraphics) => {
    g.clear()
    g.lineStyle(SPINNER_LINE_WIDTH, SPINNER_COLOR, SPINNER_ALPHA)
    drawArc(g, SPINNER_RADIUS, 0, Math.PI * 0.85)
  }

  return <Graphics ref={graphicsRef} draw={draw} x={x} y={y} pivot={{ x: 0, y: 0 }} />
}

export function CanvasLoadingSpinner({ x, y, text }: CanvasLoadingProps) {
  const spinnerY = y - (text ? 34 : 0)
  return (
    <>
      <Spinner x={x} y={spinnerY} />
      {text && <Text text={text} anchor={0.5} x={x} y={y + 20} style={spinnerTextStyle} />}
    </>
  )
}
